import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";
import {
  ArrowLeft, BarChart3, Users, MessageCircle, TrendingUp, 
  ThumbsUp, ThumbsDown, Loader2, Calendar
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AGENT_NAMES = {
  routeur: "ODYS'GUIDE",
  analyse: "ODYS'ANALYSE",
  ia: "ODYS'ASSISTANT",
  strategie: "ODYS'STRATEGIE",
  nego: "ODYS'NEGO",
  banque: "ODYS'BANQUE",
  travaux: "ODYS'TRAVAUX",
  fiscalite: "ODYS'FISCALITE",
  locataire: "ODYS'LOCATAIRE",
  marche: "ODYS'MARCHE",
  juridique: "ODYS'JURIDIQUE",
  notaire: "ODYS'NOTAIRE"
};

const AGENT_COLORS = {
  routeur: "#16233B",
  analyse: "#FF8C42",
  ia: "#10B981",
  strategie: "#6366F1",
  nego: "#EC4899",
  banque: "#0EA5E9",
  travaux: "#D97706",
  fiscalite: "#7C3AED",
  locataire: "#059669",
  marche: "#14B8A6",
  juridique: "#8B5CF6",
  notaire: "#374151"
};

export default function AdminAnalyticsPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [usageRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/api/analytics/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/feedback/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setUsageData(usageRes.data);
      setFeedbackData(feedbackRes.data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  const maxAgentCount = usageData?.agent_usage?.length > 0 
    ? Math.max(...usageData.agent_usage.map(a => a.count)) 
    : 1;

  const maxDailyCount = usageData?.daily_usage?.length > 0 
    ? Math.max(...usageData.daily_usage.map(d => d.count)) 
    : 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-30 safe-top">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            <h1 className="font-semibold text-lg">Analytics</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{usageData?.totals?.students || 0}</p>
                  <p className="text-xs text-muted-foreground">Eleves</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <MessageCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{usageData?.totals?.conversations || 0}</p>
                  <p className="text-xs text-muted-foreground">Conversations</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{usageData?.totals?.messages || 0}</p>
                  <p className="text-xs text-muted-foreground">Messages</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                  <ThumbsUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{feedbackData?.satisfaction_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Satisfaction</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agent Usage Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Agents les plus utilises
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {usageData?.agent_usage?.slice(0, 10).map((agent, i) => (
                <div key={agent._id} className="flex items-center gap-3">
                  <span className="w-6 text-right text-sm text-muted-foreground">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">
                        {AGENT_NAMES[agent._id] || agent._id}
                      </span>
                      <span className="text-sm text-muted-foreground">{agent.count}</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(agent.count / maxAgentCount) * 100}%`,
                          backgroundColor: AGENT_COLORS[agent._id] || "#6B7280"
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {(!usageData?.agent_usage || usageData.agent_usage.length === 0) && (
                <p className="text-center text-muted-foreground py-8">
                  Pas encore de donnees
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Feedback by Agent */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ThumbsUp className="w-5 h-5" />
              Satisfaction par agent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {feedbackData?.by_agent && Object.entries(feedbackData.by_agent).map(([agent, stats]) => {
                const total = (stats.up || 0) + (stats.down || 0);
                const rate = total > 0 ? Math.round((stats.up / total) * 100) : 0;
                return (
                  <div 
                    key={agent}
                    className="p-4 rounded-lg border bg-card"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{AGENT_NAMES[agent] || agent}</span>
                      <span 
                        className={`text-sm font-bold ${
                          rate >= 70 ? 'text-emerald-500' : rate >= 50 ? 'text-yellow-500' : 'text-red-500'
                        }`}
                      >
                        {rate}%
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3 text-emerald-500" />
                        {stats.up || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <ThumbsDown className="w-3 h-3 text-red-400" />
                        {stats.down || 0}
                      </span>
                    </div>
                  </div>
                );
              })}
              {(!feedbackData?.by_agent || Object.keys(feedbackData.by_agent).length === 0) && (
                <p className="text-center text-muted-foreground py-8 col-span-full">
                  Pas encore de feedbacks
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Daily Usage Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Conversations par jour (30 derniers jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-end gap-1">
              {usageData?.daily_usage?.slice(-30).map((day, i) => (
                <div 
                  key={day._id}
                  className="flex-1 flex flex-col items-center group"
                >
                  <div 
                    className="w-full bg-primary/80 hover:bg-primary rounded-t transition-all cursor-pointer"
                    style={{ height: `${Math.max((day.count / maxDailyCount) * 100, 5)}%` }}
                    title={`${day._id}: ${day.count} conversations`}
                  />
                  {i % 5 === 0 && (
                    <span className="text-[8px] text-muted-foreground mt-1 transform -rotate-45">
                      {day._id.slice(5)}
                    </span>
                  )}
                </div>
              ))}
              {(!usageData?.daily_usage || usageData.daily_usage.length === 0) && (
                <p className="w-full text-center text-muted-foreground py-8">
                  Pas encore de donnees
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Total Feedback Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Resume des feedbacks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-emerald-500">
                  <ThumbsUp className="w-8 h-8" />
                  <span className="text-4xl font-bold">{feedbackData?.total?.up || 0}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Positifs</p>
              </div>
              <div className="h-16 w-px bg-border" />
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-red-400">
                  <ThumbsDown className="w-8 h-8" />
                  <span className="text-4xl font-bold">{feedbackData?.total?.down || 0}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Negatifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
